import { Component, Inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AuthModule } from './modules/auth/auth.module';
import { CustomTranslateService } from './commons/services/custom-translate.service';
import { ToastModule } from 'primeng/toast';
import { PrimeNGConfig } from 'primeng/api';
import { StoreConfigurationService } from './graphQL/services/store-configurations/store-configurations.service';
import { DOCUMENT } from '@angular/common';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    AuthModule,
    ToastModule],
  providers:[TranslateService],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css'
})export class AppComponent {
  title = 'front-base-shell';
  configurationStore: any;  
  constructor(
    private _customTranslateService: CustomTranslateService,
    private translate: TranslateService,
    private primengConfig: PrimeNGConfig,
    @Inject(DOCUMENT) private document: Document,
    private storeConfigurationsService: StoreConfigurationService
  ) {
    
    // 1. Set up translations
    this._customTranslateService.load();
    translate.setDefaultLang('es');
    
    // Set immediate fallback config with default English values
    const defaultConfig = {
      firstDayOfWeek: 1,
      dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
      dayNamesMin: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
      monthNames: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
      monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      today: 'Today',
      clear: 'Clear',
      weekHeader: 'Wk'
    };
    this.primengConfig.setTranslation(defaultConfig);

    // Load translations when language is ready
    this.loadPrimeNGTranslations();

    // Listen for language changes and reload translations
    this.translate.onLangChange.subscribe(() => {
      this.loadPrimeNGTranslations();
    });

    this.translate.onDefaultLangChange.subscribe(() => {
      this.loadPrimeNGTranslations();
    });

    // 2. Apply saved colors immediately (before making any request)
    const savedColors = localStorage.getItem('storeColors');
    if (savedColors) {
      this.applyColorsToDOM(JSON.parse(savedColors));
    }
        
    // 3. Fetch new configuration and update only if changed
    this.storeConfigurationsService.getStoreConfigurations().subscribe({
      next: (resp) => {
        this.configurationStore = resp;
        this.setColorStore();
      },
      error: (err) => {
        console.error('Error fetching store configurations:', err);
      }
    });
  }

  private loadPrimeNGTranslations(): void {
    this.translate.get(['primeng', 'mcfLoginShell.signup.calendar']).subscribe(
      (translations: any) => {
        const primengTranslations = translations['primeng'] || {};
        const calendarTranslations = translations['mcfLoginShell.signup.calendar'] || {};

        // Build final config with comprehensive fallback logic
        const config: any = {};

        // Set firstDayOfWeek (default to Monday=1 if not specified)
        config.firstDayOfWeek = calendarTranslations.firstDayOfWeek ?? 1;

        // Helper function to validate and use arrays
        const getValidArray = (value: any, fallback: string[]): string[] => {
          return Array.isArray(value) && value.length > 0 ? value : fallback;
        };

        // Set day names with cascading fallbacks
        config.dayNames = getValidArray(
          calendarTranslations.dayNames || primengTranslations.dayNames,
          ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
        );

        config.dayNamesShort = getValidArray(
          calendarTranslations.dayNamesShort || primengTranslations.dayNamesShort,
          ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
        );

        config.dayNamesMin = getValidArray(
          calendarTranslations.dayNamesMin || primengTranslations.dayNamesMin,
          ['S', 'M', 'T', 'W', 'T', 'F', 'S']
        );

        // Set month names with cascading fallbacks
        config.monthNames = getValidArray(
          calendarTranslations.monthNames || primengTranslations.monthNames,
          ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
        );

        config.monthNamesShort = getValidArray(
          calendarTranslations.monthNamesShort || primengTranslations.monthNamesShort,
          ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
        );

        // Set string properties with fallbacks
        config.today = calendarTranslations.today || primengTranslations.today || 'Today';
        config.clear = calendarTranslations.clear || primengTranslations.clear || 'Clear';
        config.weekHeader = calendarTranslations.weekHeader || primengTranslations.weekHeader || 'Wk';

        // Apply the final configuration
        this.primengConfig.setTranslation(config);
      }
    );
  }

  getStyleValue(styleTypeName: string): string {
    const style = this.configurationStore.StyleType.find((s: any) => s.StyleTypeName === styleTypeName);
    return style ? style.StyleValue : '';
  }

  public setColorStore() {
    const configColors = {
      primary: this.getStyleValue('OneColor'),
      secondary: this.getStyleValue('TwoColor'),
      titles: this.getStyleValue('ThreeColor'),
      alerts: this.getStyleValue('FourColor'),
      bodyFont: this.getStyleValue('FontColor'),
      buttonsFont: this.getStyleValue('FontButtonColor'),
      highlightColor: this.getStyleValue('OneColor')
    };

    const configColorsString = JSON.stringify(configColors);
    const savedColors = localStorage.getItem('storeColors');

    // Only apply and save if changed
    if (savedColors !== configColorsString) {
      localStorage.setItem('storeColors', configColorsString);
      this.applyColorsToDOM(configColors);
    }
  }



  private applyColorsToDOM(colors: any) {
    if (!this.document) return;
    this.document.documentElement.style.setProperty('--primary-color', colors.primary);
    this.document.documentElement.style.setProperty('--secondary-color', colors.secondary);
    this.document.documentElement.style.setProperty('--titles-color', colors.titles);
    this.document.documentElement.style.setProperty('--alerts-color', colors.alerts);
    this.document.documentElement.style.setProperty('--bodyFont-color', colors.bodyFont);
    this.document.documentElement.style.setProperty('--buttonsFont-color', colors.buttonsFont);
    this.document.documentElement.style.setProperty('--borderHighlight-color', colors.highlightColor);
  }
}
